import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import ExportButton from "../../../../components/buttons/ExportButton";
import ClearFiltersButton from "../../../../components/filters/ClearFiltersButton";
import FilterMenu from "../../../../components/filters/FilterMenu";
import SearchAndOrFilter from "../../../../components/filters/SearchAndOrFilter";
import SearchInput from "../../../../components/filters/SearchInput";
import SwitchFilter from "../../../../components/filters/SwitchFilter";
import { Toast } from "../../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import { useFileNaming } from "../../../../hooks/useFileNaming";
import {
  LogicNameFilter,
  ProtectionLevelText,
} from "../../../../utils/constants";
import { searchObjectProperties } from "../../../../utils/search";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import IntegrationRowFilter from "../components/IntegrationRowFilter";
import ProtectionLevel from "../components/ProtectionLevel";
import StepDrawer from "../components/StepDrawer";
import StepsTable from "../components/StepsTable";

export default function StepsViewModal({
  open,
  onClose,
  steps,
  filteredScenariosSets,
}) {
  const location = useLocation();
  const [currentSteps, setCurrentSteps] = useState([]);
  const [filterLogic, setFilterLogic] = useState({
    tools: LogicNameFilter.LOGIC_AND,
    controlIds: LogicNameFilter.LOGIC_OR,
    cveIds: LogicNameFilter.LOGIC_OR,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
    tools: [],
    status: [],
    showActiveDetection: false,
    controlIds: {},
    cveIds: {},
  });
  const [filterOptions, setFilterOptions] = useState({
    tools: [],
    status: ["All", ...Object.values(ProtectionLevelText)],
    controlIds: [],
    cveIds: [],
  });
  const { generateFileName } = useFileNaming();

  const maxSizeRef = useRef({
    width: window.innerWidth * 0.95,
    height: window.innerHeight * 0.95,
  });

  // Update max size when window resizes
  useEffect(() => {
    const updateMaxSize = () => {
      maxSizeRef.current = {
        width: window.innerWidth * 0.95,
        height: window.innerHeight * 0.95,
      };
    };

    window.addEventListener("resize", updateMaxSize);
    updateMaxSize(); // Initialize on mount

    return () => window.removeEventListener("resize", updateMaxSize);
  }, []);

  useEffect(() => {
    if (location.state?.status) {
      // Transform the status array into an object with the status as keys and true as values
      const statusObj = location.state.status.reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {});

      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        status: statusObj,
      }));
    }

    if (location.state?.controlID) {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        controlIds: {
          [location.state.controlID]: true,
        },
      }));
    }

    if (location.state?.cveId) {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        cveIds: {
          [location.state.cveId]: true,
        },
      }));
    }
  }, [location.state]);

  const gridRef = useGridApiRef();
  const [selectedRowIDs, setSelectedRowIDs] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasShownNotFoundToast, setHasShownNotFoundToast] = useState(false);

  const { trackEvent } = useAPI();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);

  useEffect(() => {
    const modal_step_id = searchParams.get("modal_step_id");
    if (modal_step_id && currentSteps?.length > 0) {
      if (!Number.isNaN(Number(modal_step_id))) {
        let step = currentSteps?.find(
          (step) => step.id === Number(modal_step_id)
        );
        if (step) {
          // means that the gap was found
          setSelectedStepId(modal_step_id);
          setHasShownNotFoundToast(false);
        } else if (!hasShownNotFoundToast) {
          // means that the gap was not found
          Toast.error("No step found with the given ID: " + modal_step_id);
          setHasShownNotFoundToast(true);
        }
      } else if (!hasShownNotFoundToast) {
        // If the modal_step_id is not a number, it is invalid
        Toast.error("Invalid param: " + modal_step_id + "");
        setHasShownNotFoundToast(true);
      }
    } else if (modal_step_id) {
      if (!Number.isNaN(Number(modal_step_id))) {
        if (steps && steps.length > 0) {
          let step = steps?.find((step) => step.id === Number(modal_step_id));
          if (step) {
            // means that the gap was found
            setSelectedStepId(modal_step_id);
            setHasShownNotFoundToast(false);
          } else if (!hasShownNotFoundToast) {
            // means that the gap was not found
            Toast.error("No step found with the given ID: " + modal_step_id);
            setHasShownNotFoundToast(true);
          }
        }
      }
    }
  }, [location, steps, hasShownNotFoundToast]);

  const onRowClick = (step) => {
    trackEvent(`user click on step ${step.row.id}`);
    setSearchParams({ modal_step_id: step.row.id });
  };

  const onRowSelection = (newSelection) => {
    setSelectedRowIDs(newSelection);
  };

  const handleTableExport = () => {
    const message = "User click on 'Export' button to download the steps table";
    trackEvent(message);

    const csvExportOptions = {
      fileName: generateFileName(`steps`),
    };
    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  useEffect(() => {
    // Get the step id from the url, if it exists
    if (steps && steps.length > 0) {
      const url = window.location.href;
      const urlSplit = url.split("/");
      const stepId = parseInt(urlSplit[urlSplit.length - 1]);
      if (steps && stepId) {
        const step = steps?.find((step) => step.step_id === stepId);
        if (step) {
          setSelectedStepId(step);
        }
      }
    }
  }, [steps]);

  useEffect(() => {
    if (steps) {
      const toolsOptions = [];
      steps?.map((step) => {
        step.mitigating_controls_tools_set?.map((tool) => {
          if (!toolsOptions.includes(tool)) {
            toolsOptions.push(tool);
          }
        });
      });

      const newFilterOptions = { ...filterOptions };
      newFilterOptions.tools = ["All", ...toolsOptions.sort()];
      const distinctControlIds = [
        "All",
        ...new Set(
          steps?.flatMap((step) =>
            step.mitigating_controls_ids.map((id) => id.toString())
          )
        ),
      ].sort((a, b) => a - b);
      newFilterOptions.controlIds = Array.from(distinctControlIds);

      // Get all unique CVE IDs from steps
      const distinctCveIds = [
        "All",
        ...new Set(
          steps?.flatMap((step) => step?.related_vulnerability_ids || [])
        ),
      ].sort();
      newFilterOptions.cveIds = Array.from(distinctCveIds);

      setFilterOptions(newFilterOptions);

      const newAppliedFilters = { ...appliedFilters };
      toolsOptions.map((tool) => {
        newAppliedFilters[tool] = false;
      });
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        tools: newAppliedFilters,
      }));
    }
  }, [steps]);

  useEffect(() => {
    if (steps !== undefined) {
      let filteredRows = steps;
      if (Object.values(appliedFilters.tools).includes(true)) {
        const tools_filter = Object.keys(appliedFilters.tools).filter(
          (key) => appliedFilters.tools[key] === true
        );
        if (filterLogic["tools"] === LogicNameFilter.LOGIC_OR) {
          filteredRows = filteredRows.filter((row) =>
            row.mitigating_controls_tools_set?.some((tool) =>
              tools_filter.includes(tool)
            )
          );
        } else if (filterLogic["tools"] === LogicNameFilter.LOGIC_AND) {
          filteredRows = filteredRows.filter((row) =>
            tools_filter?.every((tool) =>
              row.mitigating_controls_tools_set.includes(tool)
            )
          );
        }
      }
      if (Object.values(appliedFilters.status).includes(true)) {
        filteredRows = filteredRows.filter((row) => {
          return appliedFilters.status[row.protection_level];
        });
      }
      if (appliedFilters.showActiveDetection) {
        filteredRows = filteredRows.filter((step) => step.is_detected);
      }
      if (Object.keys(appliedFilters.controlIds).length > 0) {
        const selectedIds = Object.keys(appliedFilters.controlIds).filter(
          (key) => appliedFilters.controlIds[key] === true
        );
        if (selectedIds.length > 0) {
          if (filterLogic["controlIds"] === LogicNameFilter.LOGIC_OR) {
            filteredRows = filteredRows.filter((row) =>
              row.mitigating_controls_ids?.some((id) =>
                selectedIds.includes(id.toString())
              )
            );
          } else if (filterLogic["controlIds"] === LogicNameFilter.LOGIC_AND) {
            filteredRows = filteredRows.filter((row) =>
              selectedIds.every((id) =>
                row.mitigating_controls_ids.includes(parseInt(id))
              )
            );
          }
        }
      }

      // Apply CVE ID filter
      if (Object.keys(appliedFilters.cveIds).length > 0) {
        const selectedCveIds = Object.keys(appliedFilters.cveIds).filter(
          (key) => appliedFilters.cveIds[key] === true
        );
        if (selectedCveIds.length > 0) {
          if (filterLogic["cveIds"] === LogicNameFilter.LOGIC_OR) {
            filteredRows = filteredRows.filter((row) =>
              row.related_vulnerability_ids?.some((id) =>
                selectedCveIds.includes(id)
              )
            );
          } else if (filterLogic["cveIds"] === LogicNameFilter.LOGIC_AND) {
            filteredRows = filteredRows.filter((row) =>
              selectedCveIds.every((id) =>
                row.related_vulnerability_ids?.includes(id)
              )
            );
          }
        }
      }

      if (appliedFilters.searchString !== "") {
        const searchString = appliedFilters.searchString;

        const parsedNumber = Number(searchString);
        const isNumber = !isNaN(parsedNumber);

        filteredRows = filteredRows.filter((row) => {
          if (isNumber) {
            return row.id && row.id.toString().includes(searchString);
          } else {
            return searchObjectProperties(row, searchString.toLowerCase());
          }
        });
      }

      setCurrentSteps(filteredRows);
    }
  }, [appliedFilters, steps, filterLogic]);

  // This useEffect is to track the applied filters when they change
  useEffect(() => {
    trackEvent(`user change filters to ${JSON.stringify(appliedFilters)}`);
  }, [appliedFilters]);

  const onStepDrawerClose = () => {
    trackEvent("Step Drawer closed");
    setSelectedStepId(null);
    gridRef.current?.setRowSelectionModel([]);
    const params = new URLSearchParams(location.search);
    params.delete("modal_step_id");
    setSearchParams(params.toString());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{ zIndex: 800 }}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
        },
      }}
    >
      <DialogContent style={{ overflowY: "auto" }}>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" id="modal-title">
              Attack Steps to Mitigate
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              flex: 1,
              overflow: "auto",
            }}
          >
            <Grid container spacing={1} justifyContent={"space-between"}>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems={"center"}>
                  <SearchAndOrFilter
                    filterName={"Tool"}
                    fieldName={"tools"}
                    options={filterOptions.tools.map((tool) => {
                      if (tool === "All") {
                        return {
                          name: tool,
                          label: (
                            <Typography variant="caption">All Tools</Typography>
                          ),
                          displayName: tool,
                        };
                      } else
                        return {
                          name: tool,
                          label: <IntegrationRowFilter name={tool} />,
                          displayName: IntegrationsMapping[tool].displayName,
                        };
                    })}
                    appliedFilters={appliedFilters}
                    setFilterLogic={setFilterLogic}
                    filterLogic={filterLogic.tools}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.tools}
                  />
                  <FilterMenu
                    filterName={"Protection Level"}
                    fieldName={"status"}
                    options={filterOptions.status.map((status) => {
                      if (status === "All") {
                        return {
                          name: status,
                          label: (
                            <Typography variant="caption">
                              All Protection Levels
                            </Typography>
                          ),
                          displayName: status,
                        };
                      }

                      return {
                        name: status,
                        label: (
                          <ProtectionLevel
                            level={status}
                            tagOrFilter={"filter"}
                          />
                        ),
                        displayName: status,
                      };
                    })}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={filterOptions.status}
                  />
                  <SearchAndOrFilter
                    filterName={"Involved Control ID"}
                    fieldName={"controlIds"}
                    options={filterOptions.controlIds.map((controlID) => {
                      if (controlID === "All") {
                        return {
                          name: controlID,
                          label: (
                            <Typography variant="caption">
                              All Controls
                            </Typography>
                          ),
                          displayName: controlID,
                        };
                      }
                      return {
                        name: controlID.toString(),
                        label: (
                          <Typography variant="caption">
                            Control #{controlID}
                          </Typography>
                        ),
                        displayName: "Control #" + controlID,
                      };
                    })}
                    appliedFilters={appliedFilters}
                    setFilterLogic={setFilterLogic}
                    filterLogic={filterLogic.controlIds}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={Object.values(filterOptions.controlIds)}
                  />

                  <SearchAndOrFilter
                    filterName={"CVE ID"}
                    fieldName={"cveIds"}
                    options={filterOptions.cveIds.map((cveId) => {
                      if (cveId === "All") {
                        return {
                          name: cveId,
                          label: (
                            <Typography variant="caption">All CVEs</Typography>
                          ),
                          displayName: cveId,
                        };
                      }
                      return {
                        name: cveId,
                        label: (
                          <Typography variant="caption">{cveId}</Typography>
                        ),
                        displayName: cveId,
                      };
                    })}
                    appliedFilters={appliedFilters}
                    setFilterLogic={setFilterLogic}
                    filterLogic={filterLogic.cveIds}
                    setAppliedFilters={setAppliedFilters}
                    fullOptions={Object.values(filterOptions.cveIds)}
                  />

                  {detectionsActive && (
                    <SwitchFilter
                      filterName={"showActiveDetection"}
                      label={"Only Active Detection"}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                    />
                  )}
                  <ClearFiltersButton
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                  />
                </Stack>
              </Grid>
              <Grid item xs={3}>
                <SearchInput
                  fieldName={"searchString"}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                />
              </Grid>
              <Grid item>
                <ExportButton
                  sx={{ ml: "5px" }}
                  handleExport={handleTableExport}
                />
              </Grid>
            </Grid>
            <Box sx={{ flex: 1, overflow: "auto" }}>
              <StepsTable
                loading={false}
                steps={currentSteps}
                onRowClick={onRowClick}
                onRowSelection={onRowSelection}
                selectedRowIDs={selectedRowIDs}
                refToPass={gridRef}
                rowNumber={6}
                sx={{ minHeight: "425px" }}
                filteredScenariosSets={filteredScenariosSets}
              />
            </Box>
            <StepDrawer
              selectedStepId={selectedStepId}
              onStepDrawerClose={() => {
                onStepDrawerClose();
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
